import * as amplitude from '@amplitude/analytics-browser';
import { trackEvent } from '@intercom/messenger-js-sdk';
import type {
	AmplitudeTrackPayload,
	Collection as CollectionResponse,
	MindItem,
	PageSource,
	PromptType,
	SubscriptionResponse,
	TemplatePrompts,
} from '@kimuno/api-sdk';
import merge from 'lodash.merge';
import TagManager from 'react-gtm-module';

import {
	AiAssistantActionPerformed,
	type AiAssistantActionPerformedProperties,
	AiAssistantFeedbackSubmitted,
	type AiAssistantFeedbackSubmittedProperties,
	AiAssistantFileUploaded,
	AiAssistantOpened,
	AiAssistantPastChatOpened,
	AiAssistantResponseReceived,
	type AiAssistantResponseReceivedProperties,
	AiAssistantSuggestionApplied,
	type AiAssistantSuggestionAppliedProperties,
	AiAssistantSuggestionGenerated,
	type AiAssistantSuggestionGeneratedProperties,
	AiAssistantTemplateOpened,
	ampli,
	CollectionCreated,
	CollectionDeleted,
	CollectionEdited,
	CollectionFollowed,
	CollectionUnfollowed,
	CollectionViewed,
	CorrectionStepCompleted,
	type CorrectionStepCompletedProperties,
	DashboardViewed,
	FeatureSearchSubmitted,
	MessageDisplayed,
	PricingPlansViewed,
	SubscriptionCancellationSelected,
	SubscriptionPauseSelected,
	SubscriptionPauseSubmitted,
	TextEdited,
	TextEditorFileExported,
	type TextEditorFileExportedProperties,
	TextEditorOpened,
	VideoUnlocked,
} from '../ampli';
import api from '../clients/api';
import { type amplitudeContentTypes, amplitudeEvents, campaign } from '../constants';
import store from '../store';
import type { Collection, CollectionProperties } from '../types/AppContent';
import type {
	AmpliEvent,
	AmpliEventUserProperties,
	EventInputData,
	RequiredProperty,
	TrackingEvent,
} from '../types/tracking';
import type { User } from '../types/User';
import * as ampliUtils from '../utils/ampli';
import {
	getAmpliContentData,
	getAmpliDeviceId,
	getAmpliSessionId,
	mapAmpliUserProperties,
} from '../utils/ampli';
import {
	getAttributionFromURLOrCookie,
	getInitialUTMProperties,
	getLastTouchUTMProperties,
} from '../utils/attributionAmplitude';
import { notifyBugsnagNetwork } from '../utils/bugsnagClient';
import { isFeatureEnabled } from '../utils/feature';
import propertyMappers from './tracking/propertyMappers';
import { trackingEvents } from './tracking/trackConfig';

const populateTrackingData = async (
	// biome-ignore lint/style/useDefaultParameterLast: parameter order is a fixed contract (Redux reducer signature / existing call sites)
	requiredProperties: RequiredProperty[] = [],
	inputData: EventInputData
) => {
	// biome-ignore lint/suspicious/useIterableCallbackReturn: pre-existing callback shape; changing returns needs per-site behavior review
	const promises = requiredProperties.map((property) => {
		const mapperFunc = propertyMappers[property];
		if (!mapperFunc) {
			return;
		}

		return mapperFunc(inputData[property]);
	});

	let flatResult = {};
	const result = await Promise.all(promises);
	requiredProperties.forEach((_, index) => {
		flatResult = Object.assign(flatResult, result[index]);
	});

	return flatResult;
};

const pageTrack = (location: string, eventInputData: EventInputData) => {
	const eventConfig: TrackingEvent = {
		requiredData: ['user'],
	};

	return populateTrackingData(eventConfig.requiredData, eventInputData).then((requiredData) => {
		gtmPageTrack(location, requiredData);
	});
};

const eventTrack = (eventConfig: TrackingEvent, eventInputData: EventInputData = {}) => {
	const DEFAULT_FROM = window.location.pathname;

	const defaultPayload = {
		from: eventInputData.from || DEFAULT_FROM,
	};

	return populateTrackingData(
		eventConfig.requiredData,
		merge(eventConfig.defaultPayload, eventInputData)
	).then((requiredData) => {
		const payload = Object.assign(defaultPayload, requiredData);

		if (isFeatureEnabled('intercom') && eventConfig.intercom) {
			intercomEventTrack(eventConfig.intercom, payload);
		}

		if (
			isFeatureEnabled('googleAnalytics') &&
			eventConfig.gtm &&
			eventConfig.gtm === trackingEvents.registerPremiumFinish.gtm
		) {
			gtmEventTrack(eventConfig.gtm, payload);
		}
	});
};

// The flattened property-mapper output pushed to GTM: arbitrary mapped keys,
// of which only `user` is read directly (for the utm mapper) — it feeds the
// utm mapper but must never itself land in the dataLayer.
type GtmEventPayload = Record<string, unknown> & { user?: User };

const gtmEventTrack = (eventName: string, eventPayload: GtmEventPayload) => {
	const { user, ...payload } = eventPayload;
	const utm = propertyMappers.utm(user);
	const dataLayer = {
		...payload,
		...utm,
		event: 'GAevent',
		event_name: eventName,
	};

	TagManager.dataLayer({ dataLayer });
};

const gtmPageTrack = (location: string, payload: any) => {
	const utm = propertyMappers.utm(payload.user);
	const dataLayer = Object.assign(payload, utm);
	dataLayer.event = 'VirtualPageview';
	dataLayer.virtualPageURL = location;

	TagManager.dataLayer({ dataLayer });
};

const intercomEventTrack = (eventName: string, eventPayload: any) => {
	trackEvent(eventName, { ...eventPayload });
};

const getUserAndSubscriptionForAmplitudeEvents = (userOptions?: AmpliEventUserProperties) => {
	const { me, subscription } = store.getState()?.user ?? null;

	// Need to overwrite some values here, because values from the userOptions have priority.
	// We use "userOptions' object in the profile creation step and some values can be a result
	// of data processing in the previous steps.
	const result = {
		user: {
			...me,
			id: userOptions?.id || me?._id,
			signUpMethod: userOptions?.signUpMethod || me?.registration?.method,
			createdAt: userOptions?.createdAt || me?.created?.timestamp,
			licenceId: userOptions?.licenceId || subscription?.licence?.licenceModelId,
			isTrainee: userOptions?.isTrainee || me?.isTrainee,
			classes: userOptions?.classes || me?.classes,
			type: userOptions?.type || me?.type,
			subjects: userOptions?.subjects || me?.subjects,
			schoolType: userOptions?.schoolType || me?.schoolType,
			isSubjectSelected: userOptions?.isSubjectSelected,
			schoolName: userOptions?.schoolName || me?.school?.name,
			role: userOptions?.role || me?.teacherRoles,
			schoolStage: userOptions?.schoolStage || me?.schoolStage,
		},
		subscription,
	};

	return result;
};

export const identifyWithAttribution = (
	userId: string,
	baseUserProperties: Record<string, any>
) => {
	const identifyObj = new amplitude.Identify();

	const { firstTouch, lastTouch } = getAttributionFromURLOrCookie();
	const initialProps = getInitialUTMProperties(firstTouch);
	const lastTouchProps = getLastTouchUTMProperties(lastTouch);

	Object.entries(initialProps).forEach(([key, value]) => {
		identifyObj.setOnce(key, value);
	});

	Object.entries(lastTouchProps).forEach(([key, value]) => {
		identifyObj.set(key, value);
	});

	Object.entries(baseUserProperties).forEach(([key, value]) => {
		if (value !== null && value !== undefined) {
			identifyObj.set(key, value);
		}
	});

	ampli.client.identify(identifyObj, { user_id: userId });
};

export const ampliEventTrack = (options: AmpliEvent) => {
	if (isFeatureEnabled('ampli')) {
		// `ampli.client` dereferences the underlying Amplitude instance, which only
		// exists after `ampli.load()`. Outside the app (Storybook, a component test
		// without the SDK boot) `identify`/`flush` would throw on `undefined`.
		if (!ampli.isLoaded) return;

		const { event, userProperties } = options;
		const { user, subscription } = getUserAndSubscriptionForAmplitudeEvents(userProperties);

		if (user?.id) {
			const baseUserProperties = {
				...mapAmpliUserProperties(user),
				...ampliUtils.getAmpliSubscriptionUserProperties(subscription as SubscriptionResponse),
			};

			identifyWithAttribution(user.id, baseUserProperties);
		}

		ampli.track(event);
		ampli.client.flush(); // Call flush() to immediately send any pending events.
	}
};

export const ampliTrackSubscriptionCancellationSelectedEvent = (
	// biome-ignore lint/style/useDefaultParameterLast: parameter order is a fixed contract (Redux reducer signature / existing call sites)
	isTrial = false,
	source: string,
	type?: string
) => {
	const eventProperties = {
		'page source': source,
		trial: isTrial === true ? 'yes' : 'no',
		type,
	};

	ampliEventTrack({
		event: new SubscriptionCancellationSelected(eventProperties as any),
	});
};

export const trackCollectionEventToAmplitude = (
	collection: Collection | CollectionResponse,
	eventType: 'created' | 'edited'
) => {
	const EventType = eventType === 'created' ? CollectionCreated : CollectionEdited;
	const eventProperties = ampliUtils.getAmpliCommonCollectionProperties(collection);
	const searchId = ampliUtils.getSearchIdWithFallback();
	ampliEventTrack({
		event: new EventType({
			...eventProperties,
			...(searchId && eventType === 'created' && { 'search id': searchId }),
		}),
	});
};

export const ampliTrackCollectionViewedEvent = (collection: Collection | CollectionResponse) => {
	const eventProperties = ampliUtils.getAmpliCommonCollectionProperties(collection);
	ampliEventTrack({
		event: new CollectionViewed({
			...eventProperties,
			'collection type': collection.isPublic ? 'curated' : 'private',
		}),
	});
};

export const ampliTrackCollectionDeletedEvent = (collection: CollectionProperties) => {
	const eventProperties = ampliUtils.getAmpliCommonCollectionProperties(collection);
	ampliEventTrack({
		event: new CollectionDeleted(eventProperties),
	});
};

export const ampliTrackSubscriptionPauseSelectedEvent = (source: string) => {
	ampliEventTrack({
		event: new SubscriptionPauseSelected({
			'page source': source,
		}),
	});
};

export const ampliTrackSubscriptionPauseSubmittedEvent = (pauseDuration: number) => {
	ampliEventTrack({
		event: new SubscriptionPauseSubmitted({
			'pause duration': `${pauseDuration}mo`,
		}),
	});
};

export const ampliTrackPricingPlansViewedEvent = (
	conversionPath: string,
	subscriptionStatus?: string
) => {
	if (subscriptionStatus === 'Suspended') {
		return;
	}

	ampliEventTrack({
		event: new PricingPlansViewed({
			'conversion path': conversionPath,
		}),
	});
};

/**
 * Tracks a collection follow or unfollow event in Amplitude.
 *
 * @param {Object} collection - The collection being followed or unfollowed.
 * @param {User} [user] - The user performing the follow or unfollow action.
 * @param {SubscriptionResponse} [subscription] - The user's subscription information.
 * @param {boolean} [isFollowed=true] - Whether the collection is being followed or unfollowed.
 */
export const ampliTrackCollectionFollowEvent = (
	collection: CollectionProperties,
	isFollowed = true
) => {
	const eventProperties = ampliUtils.getAmpliCommonCollectionProperties(collection);
	const event = isFollowed
		? new CollectionFollowed(eventProperties)
		: new CollectionUnfollowed(eventProperties);
	ampliEventTrack({
		event,
	});
};

/**
 * Tracks a video unlocked event in Amplitude.
 *
 * @param {MindItem} mindItem - The content that should be tracked.
 * @param {User} [user] - The user performing the follow or unfollow action.
 * @param {SubscriptionResponse} [subscription] - The user's subscription information.
 */
export const ampliTrackVideoUnlockedEvent = (mindItem: MindItem) => {
	const eventProperties = {
		...ampliUtils.getAmpliContentEventPayload(mindItem, 'mindItem'),
	};

	ampliEventTrack({
		event: new VideoUnlocked(eventProperties as any),
	});
};

export const ampliTrackAIAssistantRequestSentEvent = ({
	mindItem,
	conversationId,
	requestId,
	promptType,
	prompt,
	attachmentsExtensions,
	pageAmount,
	pageSource,
	templateParameters,
	studentSubmissions,
}: {
	mindItem?: MindItem;
	conversationId: string;
	requestId: string;
	promptType: PromptType;
	prompt: string | TemplatePrompts;
	attachmentsExtensions: string[];
	pageAmount: number | null;
	pageSource: string;
	templateParameters?: Record<string, string | number>[];
	studentSubmissions?: number;
}) => {
	const searchId = ampliUtils.getSearchIdWithFallback();
	const eventProperties: AmplitudeTrackPayload = {
		session_id: getAmpliSessionId() ?? 0,
		device_id: getAmpliDeviceId() ?? '',
		event_type: amplitudeEvents.AIAssistantRequestSent,
		event_properties: {
			content: mindItem ? getAmpliContentData(mindItem, 'mindItem') : [],
			'conversation id': conversationId,
			'prompt type': promptType,
			'request id': requestId,
			'page source': pageSource as PageSource,
			...(promptType === 'template'
				? {
						'prompt template': prompt as keyof TemplatePrompts,
					}
				: {
						'user prompt': prompt,
					}),
			...(attachmentsExtensions.length > 0 ? { 'file type': attachmentsExtensions } : {}),
			...(pageAmount ? { 'page amount': pageAmount } : {}),
			...(searchId && { 'search id': searchId }),
			...(templateParameters && templateParameters.length > 0
				? { 'template parameters': templateParameters }
				: {}),
			...(studentSubmissions !== undefined ? { 'student submissions': studentSubmissions } : {}),
		},
	};

	ampliServerSideEventTrack(false, eventProperties);
};

export const ampliTrackCorrectionStepCompletedEvent = ({
	conversationId,
	step,
	contextChoice,
	templateParameters,
}: {
	conversationId: string;
	step: CorrectionStepCompletedProperties['step'];
	contextChoice?: CorrectionStepCompletedProperties['context choice'];
	templateParameters: Record<string, string | number>[];
}) => {
	const eventProperties: CorrectionStepCompletedProperties = {
		'conversation id': conversationId,
		step,
		...(contextChoice ? { 'context choice': contextChoice } : {}),
		...(templateParameters.length > 0 ? { 'template parameters': templateParameters } : {}),
	};

	ampliEventTrack({
		event: new CorrectionStepCompleted(eventProperties),
	});
};

export const ampliTrackAIAssistantResponseReceivedEvent = ({
	mindItem,
	conversationId,
	requestId,
	pageSource,
	documentGenerated,
	imageGenerated,
	podcastGenerated,
	promptTemplate,
}: {
	mindItem?: MindItem;
	conversationId: string;
	requestId: string;
	pageSource: string;
	documentGenerated: boolean;
	imageGenerated: boolean;
	podcastGenerated: boolean;
	// The template the matching Request Sent carried, so a funnel can hold
	// `prompt template` constant across both; absent on custom prompts.
	promptTemplate?: TemplatePrompts;
}) => {
	const feature: string[] = [];
	if (documentGenerated) feature.push('document generation');
	if (imageGenerated) feature.push('image generation');
	if (podcastGenerated) feature.push('podcast generation');

	const eventProperties: AiAssistantResponseReceivedProperties = {
		content: mindItem ? getAmpliContentData(mindItem, 'mindItem') : [],
		'conversation id': conversationId,
		'request id': requestId,
		'page source': pageSource,
		feature,
		...(promptTemplate ? { 'prompt template': promptTemplate } : {}),
	};

	ampliEventTrack({
		event: new AiAssistantResponseReceived(eventProperties),
	});
};

export const ampliTrackAIAssistantFeedbackSubmittedEvent = ({
	conversationId,
	requestId,
	feedbackValue,
	promptType,
	prompt,
	pageSource,
	feature,
}: {
	conversationId: string;
	requestId: string;
	feedbackValue: string;
	promptType: AiAssistantFeedbackSubmittedProperties['prompt type'];
	prompt: string | TemplatePrompts;
	pageSource?: string;
	feature: string[];
}) => {
	const eventProperties: AiAssistantFeedbackSubmittedProperties = {
		'conversation id': conversationId,
		'request id': requestId,
		'feedback value': feedbackValue as 'positive' | 'negative',
		'feedback type': 'sentiment',
		'prompt type': promptType,
		...(promptType === 'template' ? { 'prompt template': prompt } : { 'user prompt': prompt }),
		...(pageSource ? { 'page source': pageSource } : {}),
		feature,
	};

	ampliEventTrack({
		event: new AiAssistantFeedbackSubmitted(eventProperties),
	});
};

export const ampliTrackAIAssistantOpenedEvent = ({
	mindItem,
	pageSource,
}: {
	mindItem?: MindItem;
	pageSource: string;
}) => {
	const eventProperties = {
		content: mindItem ? getAmpliContentData(mindItem, 'mindItem') : [],
		'page source': pageSource,
	};
	ampliEventTrack({
		event: new AiAssistantOpened(eventProperties),
	});
};

// Fires "AI Assistant Template Opened" once per template open: a KI Tools card
// click, a dashboard launch link, a direct link or reload of a template URL, a
// reader template card, or the Korrekturhilfe create page. `promptTemplate` is a
// string rather than `TemplatePrompts` because the Korrekturhilfe entry is not a
// template prompt but shares the funnel.
export const ampliTrackAIAssistantTemplateOpenedEvent = ({
	promptTemplate,
	pageSource,
}: {
	promptTemplate: string;
	pageSource: string;
}) => {
	ampliEventTrack({
		event: new AiAssistantTemplateOpened({
			'prompt template': promptTemplate,
			'page source': pageSource,
		}),
	});
};

// Fires the "Feature Search Submitted" event when a user submits a search
// query (Enter / icon click) on a feature surface — currently only Meine
// Inhalte.
export const ampliTrackFeatureSearchSubmittedEvent = ({
	pageSource,
	searchTerm,
}: {
	pageSource: string;
	searchTerm: string;
}) => {
	ampliEventTrack({
		event: new FeatureSearchSubmitted({
			'page source': pageSource,
			'search term': searchTerm,
		}),
	});
};

// Fires the "AI Assistant Past Chat Opened" event when a user re-opens a
// conversation — either from the Verlauf page, from an artifact card on Meine
// Inhalte, or from the assistant's sidebar history list.
export const ampliTrackAIAssistantPastChatOpenedEvent = ({
	conversationId,
	pageSource,
	contentType,
}: {
	conversationId: string;
	pageSource?: string;
	contentType?: amplitudeContentTypes;
}) => {
	ampliEventTrack({
		event: new AiAssistantPastChatOpened({
			'conversation id': conversationId,
			...(pageSource ? { 'page source': pageSource } : {}),
			...(contentType ? { content: [{ 'content type': contentType }] } : {}),
		}),
	});
};

export const ampliTrackAIAssistantActionPerformedEvent = ({
	conversationId,
	requestId,
	pageSource,
	feature,
	type,
	userPrompt,
}: {
	conversationId: string;
	requestId: string;
	pageSource: string;
	feature: AiAssistantActionPerformedProperties['feature'];
	type?: string;
	userPrompt?: string;
}) => {
	const eventProperties: AiAssistantActionPerformedProperties = {
		'conversation id': conversationId,
		'request id': requestId,
		'page source': pageSource,
		feature,
		...(type && { type }),
		...(userPrompt && { 'user prompt': userPrompt }),
	};
	ampliEventTrack({
		event: new AiAssistantActionPerformed(eventProperties),
	});
};

export const ampliServerSideEventTrack = (
	isPublic: boolean,
	payload: AmplitudeTrackPayload,
	userId?: string,
	includeAttributionInEventProps = false
) => {
	if (!isFeatureEnabled('ampli')) return;
	const { user, subscription } = getUserAndSubscriptionForAmplitudeEvents();

	const { firstTouch, lastTouch } = getAttributionFromURLOrCookie();
	const initialUTM = getInitialUTMProperties(firstTouch);
	const lastTouchUTM = getLastTouchUTMProperties(lastTouch);
	const attributionProps = { ...initialUTM, ...lastTouchUTM };

	const enrichedPayload = {
		...payload,
		user_id: user?.id || userId,
		user_properties: user?.id
			? {
					...mapAmpliUserProperties(user),
					...ampliUtils.getAmpliSubscriptionUserProperties(subscription),
					...attributionProps,
				}
			: { ...attributionProps },
		...(includeAttributionInEventProps
			? {
					event_properties: {
						...payload.event_properties,
						...attributionProps,
					},
				}
			: {}),
	};

	notifyBugsnagNetwork('track', 'ampliServerSideEventTrack', { isPublic, payload: enrichedPayload });
	api.track.trackEvent(isPublic, enrichedPayload);
};

export const MessageType = {
	DIALOG: 'dialog',
	BANNER: 'banner',
	HINT: 'hint',
} as const;

export type MessageType = (typeof MessageType)[keyof typeof MessageType];

export const ampliTrackMessageDisplayedEvent = (
	campaignName: string,
	messageType: MessageType = MessageType.DIALOG,
	walkthroughStep?: number
) => {
	ampliEventTrack({
		event: new MessageDisplayed({
			campaign: campaignName,
			'message type': messageType,
			'walkthrough step': walkthroughStep,
		}),
	});
};

export const ampliTrackAISuggestionsGeneratedEvent = (
	type: AiAssistantSuggestionGeneratedProperties['suggestion type']
) => {
	ampliEventTrack({
		event: new AiAssistantSuggestionGenerated({
			'suggestion type': type,
		}),
	});
};

export const ampliTrackAISuggestionsAppliedEvent = (
	type: AiAssistantSuggestionAppliedProperties['suggestion type']
) => {
	ampliEventTrack({
		event: new AiAssistantSuggestionApplied({
			'suggestion type': type,
		}),
	});
};

export const ampliTrackAIAssistantFileUploadedEvent = (extension: string) => {
	ampliEventTrack({
		event: new AiAssistantFileUploaded({
			'file type': [extension],
		}),
	});
};

export const ampliTrackTextEditorOpenedEvent = (
	conversationId: string,
	textSource: 'document' | 'chat',
	pageSource: string
) => {
	ampliEventTrack({
		event: new TextEditorOpened({
			'conversation id': conversationId,
			'text source': textSource,
			'page source': pageSource,
		}),
	});
};

export type TextEditorExportFormat = 'pdf' | 'docx' | 'wav' | 'mp3';

// Carries no `prompt template` yet: the tracking plan has no such property on
// this event, and the export call sites only know the conversation id (WF-314).
export const ampliTrackTextEditorFileExportedEvent = ({
	conversationId,
	exportType,
	exportFormat,
	feature,
}: {
	conversationId: string;
	exportType: 'download' | 'share' | 'print';
	// Omitted only for 'share': a public link has no file format.
	exportFormat?: TextEditorExportFormat;
	feature?: string[];
}) => {
	const properties: TextEditorFileExportedProperties = {
		'conversation id': conversationId,
		'export type': exportType,
		...(exportFormat ? { 'export format': exportFormat } : {}),
		...(feature ? { feature } : {}),
	};

	ampliEventTrack({
		event: new TextEditorFileExported(properties),
	});
};

export const ampliTrackTextEditedEvent = (conversationId: string, pageSource: string) => {
	ampliEventTrack({
		event: new TextEdited({
			'conversation id': conversationId,
			'page source': pageSource,
		}),
	});
};

// Fires the governed "Message Displayed" event when the Trial Report Card dialog
// opens, so the dialog joins the shared Message-Displayed funnel. The three fixed
// values (campaign = notes-onboarding, message type = dialog, walkthrough step = 0)
// are encapsulated here so the component calls one zero-arg wrapper.
export const ampliTrackTrialReportCardMessageDisplayedEvent = () => {
	ampliTrackMessageDisplayedEvent(campaign.notesOnboarding, MessageType.DIALOG, 0);
};

export const ampliTrackDashboardViewedEvent = () => {
	ampliEventTrack({ event: new DashboardViewed() });
};

const service = {
	eventTrack,
	pageTrack,
	ampliEventTrack,
	ampliServerSideEventTrack,
	ampliTrackPricingPlansViewedEvent,
	ampliTrackSubscriptionCancellationSelectedEvent,
	ampliTrackCollectionDeletedEvent,
	ampliTrackSubscriptionPauseSelectedEvent,
	ampliTrackSubscriptionPauseSubmittedEvent,
	ampliTrackAIAssistantRequestSentEvent,
	ampliTrackAIAssistantResponseReceivedEvent,
	ampliTrackCorrectionStepCompletedEvent,
	ampliTrackAIAssistantFeedbackSubmittedEvent,
	ampliTrackAIAssistantOpenedEvent,
	ampliTrackAIAssistantTemplateOpenedEvent,
	ampliTrackAIAssistantPastChatOpenedEvent,
	ampliTrackFeatureSearchSubmittedEvent,
	ampliTrackAIAssistantActionPerformedEvent,
	ampliTrackAISuggestionsGeneratedEvent,
	ampliTrackAISuggestionsAppliedEvent,
	ampliTrackAIAssistantFileUploadedEvent,
	ampliTrackTextEditorOpenedEvent,
	ampliTrackTextEditorFileExportedEvent,
	ampliTrackTextEditedEvent,
	ampliTrackTrialReportCardMessageDisplayedEvent,
	ampliTrackDashboardViewedEvent,
};

export default service;
